import { useEffect, useState } from 'react';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const PLAYERS_ENDPOINT = 'https://icy-silence-a2e7.1337sunway.workers.dev/api/players/default';

const normalizePlayers = payload => {
  if (!Array.isArray(payload)) return [];

  return payload
    .filter(item => item && typeof item.name === 'string')
    .map(item => ({
      id: item.uuid || item.name,
      name: item.name
    }));
};

const createHistory = ({ points, now, playersOnline, maxPlayers }) => {
  const history = [];
  const basePlayers = clamp(playersOnline, 0, maxPlayers);

  for (let i = points - 1; i >= 0; i -= 1) {
    const timestamp = now - i * 60_000;
    const wave = Math.sin((timestamp / 1000 / 60) * 0.5) * 7;
    const jitter = (Math.random() - 0.5) * 6;
    const players = clamp(Math.round(basePlayers + wave + jitter), 0, maxPlayers);
    const cpu = clamp(Math.round(18 + (players / maxPlayers) * 55 + Math.random() * 6), 8, 92);
    const tps = clamp(Number((19.2 + (1 - cpu / 100) * 1.1 + (Math.random() - 0.5) * 0.15).toFixed(2)), 16.0, 20.0);

    history.push({ timestamp, players, cpu, tps });
  }

  return history;
};

export const useServerTelemetry = () => {
  const [state, setState] = useState(() => {
    const now = Date.now();
    const maxPlayers = 32;
    const playersOnline = 0;
    const pingMs = Math.floor(5 + Math.random() * 26);

    return {
      serverName: 'Kweebec World',
      address: 'world.kweebec.xyz',
      region: 'EU',
      version: 'Early Access',
      status: 'online',
      pingMs,
      maxPlayers,
      playersOnline,
      queue: 0,
      history: createHistory({ points: 30, now, playersOnline, maxPlayers }),
      players: [],
      updatedAt: now
    };
  });

  useEffect(() => {
    const interval = window.setInterval(() => {
      setState(previous => {
        const now = Date.now();
        const maxPlayers = previous.maxPlayers;
        const playersOnline = clamp(previous.playersOnline, 0, maxPlayers);
        const drift = (Math.random() - 0.42) * 5;
        const demandPlayers = clamp(
          Math.round(playersOnline + drift + Math.max(0, (Math.random() - 0.78) * 55)),
          0,
          maxPlayers + 40
        );
        const cpu = clamp(Math.round(18 + (playersOnline / maxPlayers) * 58 + Math.random() * 6), 8, 94);
        const tps = clamp(Number((19.25 + (1 - cpu / 100) * 1.05 + (Math.random() - 0.5) * 0.2).toFixed(2)), 15.8, 20.0);

        const history = [...previous.history, { timestamp: now, players: playersOnline, cpu, tps }].slice(-30);

        return {
          ...previous,
          queue: clamp(Math.round(Math.max(0, demandPlayers - maxPlayers)), 0, 35),
          history,
          updatedAt: now
        };
      });
    }, 4_000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const loadPlayers = async () => {
      try {
        const response = await fetch(PLAYERS_ENDPOINT, { signal: controller.signal, cache: 'no-store' });
        if (!response.ok) return;
        const json = await response.json();
        const players = normalizePlayers(json);
        if (cancelled) return;

        setState(previous => {
          const now = Date.now();
          const maxPlayers = previous.maxPlayers;
          const playersOnline = clamp(players.length, 0, maxPlayers);

          return {
            ...previous,
            players,
            playersOnline,
            history: createHistory({ points: 30, now, playersOnline, maxPlayers }),
            updatedAt: now
          };
        });
      } catch {
        if (!cancelled) {
          setState(previous => ({
            ...previous,
            players: [],
            playersOnline: 0,
            updatedAt: Date.now()
          }));
        }
      }
    };

    loadPlayers();
    const interval = window.setInterval(loadPlayers, 10_000);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(interval);
    };
  }, []);

  return state;
};
